import { FC } from 'react'
import { Draggable, Droppable } from 'react-beautiful-dnd'
import { Row } from '@mobileTypes'
import { Close as CloseIcon, DragIndicator } from '@mui/icons-material'
import { Box, Chip, IconButton, Paper, Tooltip, Typography } from '@mui/material'
import { grey } from '@mui/material/colors'

interface RowContainerProps {
  row: Row
  rowIndex: number
  onDeleteRow: () => void
  onEditElement: (rowIndex: number, elementIndex: number) => void
  onDeleteElement: (rowIndex: number, elementIndex: number) => void
}

export const RowContainer: FC<RowContainerProps> = ({
  row,
  rowIndex,
  onDeleteRow,
  onEditElement,
  onDeleteElement,
}) => {
  const droppableId = `ROW:${rowIndex}`

  return (
    <Draggable draggableId={`ROW_DRAG:${rowIndex}`} index={rowIndex}>
      {(providedRow, snapshotRow) => (
        <Paper
          ref={providedRow.innerRef}
          {...providedRow.draggableProps}
          sx={{
            p: 2,
            mb: 2,
            backgroundColor: snapshotRow.isDragging ? 'action.selected' : 'background.paper',
            border: theme => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Box
              {...providedRow.dragHandleProps}
              sx={{ display: 'flex', alignItems: 'center', cursor: 'grab' }}
            >
              <DragIndicator fontSize='small' sx={{ color: grey[600], mr: 1 }} />
              <Typography variant='subtitle2' color='text.secondary'>
                Row {rowIndex + 1}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }} />
            <IconButton size='small' onClick={onDeleteRow} color='error'>
              <CloseIcon fontSize='small' />
            </IconButton>
          </Box>

          <Droppable droppableId={droppableId} type='ELEMENTS' direction='horizontal'>
            {(provided, snapshot) => (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  minHeight: 50,
                  p: 1.5,
                  backgroundColor: snapshot.isDraggingOver
                    ? 'action.hover'
                    : 'action.disabledBackground',
                  borderRadius: 1,
                  border: theme =>
                    `2px dashed ${
                      snapshot.isDraggingOver ? theme.palette.primary.main : theme.palette.divider
                    }`,
                }}
              >
                {row.elements.length === 0 && (
                  <Typography
                    variant='body2'
                    color='text.disabled'
                    sx={{ width: '100%', textAlign: 'center', py: 1 }}
                  >
                    Drop fields here
                  </Typography>
                )}
                {row.elements.map((element, elementIndex) => (
                  <Draggable
                    key={`${element.code}_${elementIndex}`}
                    draggableId={`ELEMENT:${rowIndex}:${elementIndex}`}
                    index={elementIndex}
                  >
                    {(providedElement, snapshotElement) => (
                      <Tooltip title={`Click to edit`}>
                        <Chip
                          ref={providedElement.innerRef}
                          {...providedElement.draggableProps}
                          {...providedElement.dragHandleProps}
                          label={element.title || element.code}
                          onClick={e => {
                            e.stopPropagation()
                            onEditElement(rowIndex, elementIndex)
                          }}
                          onDelete={e => {
                            e.stopPropagation()
                            onDeleteElement(rowIndex, elementIndex)
                          }}
                          sx={{
                            backgroundColor: snapshotElement.isDragging
                              ? 'primary.light'
                              : element.bindType === 'js'
                              ? 'warning.light'
                              : 'background.paper',
                            cursor: 'pointer',
                          }}
                        />
                      </Tooltip>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </Paper>
      )}
    </Draggable>
  )
}
